import React, { Fragment, useEffect, useState, useContext } from 'react';
import moment from 'moment';
import DeleteForeverIcon from '@material-ui/icons/Delete';
import { Box, Button, CircularProgress, Grid, Typography } from '@material-ui/core';

import { useStyles } from './styles';
import { IProductReturn } from './interfaces';
import { Can } from '../../../components/util/Can';
import { AuthContext } from 'contexts/AuthContext';
import { useEnvironment, useSnackbar } from '../../../hooks';
import { useQualityReturnReceiptGql } from './graphql/returnReceipt';
import { ModalDelete } from '../productReturn/components/modalDelete';
import { PageBarHeader } from '../../../components/migrated/pageheader';
import { useQualityReturnPictureListGql } from './graphql/returnPictureList';
import { ImageDownloadButton } from '../productReturn/components/imageDownloadButton';
import { useQualityReturnReceiptsGql } from '../productReturn/graphql/allReturnReceipts';

export const ProductReturnView = (props: IProductReturn) => {
  const {
    user: { isVisaoTok }
  } = useContext(AuthContext);
  const { router } = props;
  const personId = +router.params.personId;
  const returnReceiptId = +router.params.returnReceiptId;

  const classes = useStyles();
  const { DEBUG } = useEnvironment();
  const { Snackbar, toggleSnackbar } = useSnackbar();
  const [showModalDelete, setShowModalDelete] = useState(false);

  const {
    runQualityReturnPictureListGql,
    dataQualityReturnPictureList,
    errorQualityReturnPictureList,
    loadingQualityReturnPictureList
  } = useQualityReturnPictureListGql();

  const {
    runQualityReturnReceiptGql,
    dataQualityReturnReceipt,
    errorQualityReturnReceipt,
    loadingQualityReturnReceipt
  } = useQualityReturnReceiptGql();

  const { runQualityReturnReceiptsGql } = useQualityReturnReceiptsGql();

  const handleOpenDeleteModal = () => setShowModalDelete(true);
  const handleCloseDeleteModal = () => setShowModalDelete(false);

  useEffect(() => {
    if (errorQualityReturnReceipt || errorQualityReturnPictureList) {
      if (DEBUG === 'TRUE') {
        const msg = errorQualityReturnReceipt || errorQualityReturnPictureList;
        console.log(`Erro ao carregar dados! Error:(message: ${msg} )`);
      } else {
        toggleSnackbar({
          message: 'Ocorreu um problema ao carregar informações. Por favor, informe ao suporte.',
          type: 'error'
        });
      }
    }
  }, [errorQualityReturnReceipt, errorQualityReturnPictureList]);

  useEffect(() => {
    runQualityReturnReceiptGql({ variables: { returnReceiptId: Number(returnReceiptId) } });
    runQualityReturnPictureListGql({
      variables: { personId: Number(personId), returnReceiptId: Number(returnReceiptId) }
    });
  }, []);

  const handleGoBack = () => {
    router.push({
      pathname: '/qualidade/devolucao',
      state: { personId }
    });
  };

  const updateTableData = () => {
    handleGoBack();
    runQualityReturnReceiptsGql({
      variables: {
        personId: +personId,
        initialRow: 0,
        nextRows: 5
      }
    });
  };

  return (
    <>
      <Snackbar />
      <ModalDelete
        open={showModalDelete}
        handleClose={handleCloseDeleteModal}
        personId={personId}
        returnReceiptId={returnReceiptId}
        updateData={() => updateTableData()}
      />
      <PageBarHeader goBack={() => handleGoBack()} title="Ver Relatório" />
      <div className={classes.container}>
        <Can permissions={['filtro-fornecedor']}>
          <></>
        </Can>

        {loadingQualityReturnReceipt && loadingQualityReturnPictureList && (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        )}

        {!loadingQualityReturnReceipt && !loadingQualityReturnPictureList && (
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={1}>
              {isVisaoTok && (
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                  <Button
                    className={classes.colorButton}
                    variant="contained"
                    size="small"
                    onClick={handleOpenDeleteModal}
                  >
                    <DeleteForeverIcon /> Excluir
                  </Button>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography>
                  <b>FORNECEDOR</b>
                </Typography>
                <Typography>
                  {personId} - {dataQualityReturnReceipt?.supplierName}
                </Typography>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <Typography>
                  <b>PRODUTO DEVOLVIDO</b>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>ID Prod: {dataQualityReturnReceipt?.productId}</Typography>
                <Typography>Nome do produto: {dataQualityReturnReceipt?.productName}</Typography>
                <Typography>Divisão: {dataQualityReturnReceipt?.productDivisionName}</Typography>
                <Typography>
                  Grupo: {dataQualityReturnReceipt?.productGroupId} - {dataQualityReturnReceipt?.productGroupName}
                </Typography>
                <Typography>
                  Data do recebimento:{' '}
                  {moment(dataQualityReturnReceipt?.receiptDate, 'DD/MM/YYYY').format('DD/MM/YYYY')}
                </Typography>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <Typography>
                  <b>DESCRITIVO</b>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>Quantidade agendada: {dataQualityReturnReceipt?.scheduledQuantity}</Typography>
                <Typography>Quantidade vistoriada: {dataQualityReturnReceipt?.inspectedQuantity}</Typography>
                <Typography>Quantidade devolvida: {dataQualityReturnReceipt?.returnedQuantity}</Typography>
                <Typography>Quantidade estocada: {dataQualityReturnReceipt?.stokedQuantity}</Typography>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <Typography>
                  <b>MOTIVO DA DEVOLUÇÃO</b>
                </Typography>
                <>
                  {dataQualityReturnReceipt?.returnReceiptReasons.map((reason) => (
                    <Typography key={reason?.reasonCode} style={{ marginLeft: '12px' }}>
                      • {reason?.reasonCode} - {reason?.reasonDescription}
                    </Typography>
                  ))}
                </>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <Typography>
                  <b>OBSERVAÇÃO</b>
                </Typography>
                <Typography style={{ wordWrap: 'break-word' }}>
                  {dataQualityReturnReceipt?.noteDescription?.toUpperCase()}
                </Typography>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <Typography>
                  <b>IMAGENS</b>
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <div className={classes.imageContainer}>
                  {dataQualityReturnPictureList?.map((upload) => (
                    <div
                      key={upload.objectKey}
                      className={classes.image}
                      style={{ backgroundImage: `url(${upload.objectURL})` }}
                    >
                      <ImageDownloadButton imageKey={upload.objectKey} />
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </Box>
        )}
      </div>
    </>
  );
};
